import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { StyledButton } from "../../../shared/components/buttons/custom-button";
import CustomStepper from "../../../shared/components/stepper/steppers";
import AlertPopup from "../../../shared/components/modals/alert-moadal";
import { LoaderComponent } from "../../../shared/components/loaders/page-loader";
import useNotification from "../../../shared/hooks/useNotification";
import { useApi } from "../../../shared/hooks/useApi";
import { useTransactionCertificateStore } from "../../../store/transaction-certificates.store";

// Import models
import {
  AlternativeTransactionCertificateDetails,
  ATBuyerSellerDetails,
  ATProductInfoDetails,
  ATRawMaterialInfo,
} from "../atc-models/atc-org";

// Import initial values
import {
  initATCertificateDetails,
  initATBuyerSellerDetails,
  initATProductInfoDetails,
  initATRawMaterialInfo,
} from "../atc-models/atc-initial-values";

// Import step forms
import TransactionCertificateDetailsForm from "./atc-tabs/TransactionCertificateDetailsForm";
import BuyerSellerDetailsForm from "./atc-tabs/BuyerSellerDetailsForm";
import ProductInformationForm from "./atc-tabs/ProductInformationForm";
import RawMaterialInformationForm from "./atc-tabs/RawMaterialInformationForm";

// Service
import { atcService } from "../atc-service/atc.service";
import { ATCUpsertPayload } from "../atc-models/atc-payload";

const AlternativeTransactionCertificate: React.FC = () => {
  const { cbLicensingCode } = useTransactionCertificateStore();
  // ===================== State =====================
  const [activeStep, setActiveStep] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [atcCertificateDetails, setAtcCertificateDetails] =
    useState<AlternativeTransactionCertificateDetails>(
      {...initATCertificateDetails,tcUploadCb:cbLicensingCode}
    );
  const [atcBuyerSellerDetails, setAtcBuyerSellerDetails] =
    useState<ATBuyerSellerDetails>(initATBuyerSellerDetails);
  const [atcProductInfoDetails, setAtcProductInfoDetails] = useState<
    ATProductInfoDetails[]
  >([]);
  const [atcRawMaterialInfo, setAtcRawMaterialInfo] = useState<
    ATRawMaterialInfo[]
  >([]);

  const { atcId } = useParams();

  // ===================== Hooks =====================
  const { loading } = useApi();
  const { notify } = useNotification();
  const navigate = useNavigate();

  // ===================== Refs =====================
  const certificateDetailsRef = useRef<any>(null);
  const buyerSellerDetailsRef = useRef<any>(null);
  const productInfoRef = useRef<any>(null);
  const rawMaterialInfoRef = useRef<any>(null);

  const formRefs = [
    certificateDetailsRef,
    buyerSellerDetailsRef,
    productInfoRef,
    rawMaterialInfoRef,
  ];

  // ===================== Prefill on Edit =====================
  useEffect(() => {
    const loadATCData = async () => {
      if (!atcId) {
        // No atcId → use initial empty values
        setAtcCertificateDetails(initATCertificateDetails);
        setAtcBuyerSellerDetails(initATBuyerSellerDetails);
        setAtcProductInfoDetails([]);
        setAtcRawMaterialInfo([]);
        return;
      }

      try {
        const resp = await atcService.getATCDetails(atcId);
        if (resp?.status === 200 && resp?.data) {
          const data: any = resp.data;

          // Prefill certificate details
          setAtcCertificateDetails({
            uploadType: data.uploadType || "",
            transactionCertificateNumber: data.atcNo
              ? data.atcNo.replace(/^ATU-/, "") // removes leading "ATU-"
              : "",
            dateOfIssue: data.atcDateOfIssue || "",
            tcStandard: Array.isArray(data.atcStandard) ? data.atcStandard : (data.atcStandard ? [data.atcStandard] : []),
            tcVersionNumber: data.atcVersionNo || "",
            cbLicensingCode: data.atcCbLicensingCode || "",
            tcUploadCb: data.tcUploadCb || "",
            uploadedFile: null,
            submissionDate: data.atcSubmissionDate || "",
          });

          // Prefill buyer & seller details
          setAtcBuyerSellerDetails({
            sellerName: data.sellerName || "",
            sellerTeId: data.sellerTeid || "",
            sellerAddress: data.sellerAddress || "",
            sellerCbLicensingCode: data.atcCbLicensingCode, // optional
            buyerName: data.buyerName || "",
            buyerTeId: data.buyerTeid || "",
            buyerAddress: data.buyerAddress || "",
            buyerScNumber: data.buyerScNo || "",
          });

          // Prefill products & raw materials
          setAtcProductInfoDetails(
            Array.isArray(data.productInformation)
              ? data.productInformation.map((p: any) => ({
                  productNumber: (p.atcProductNo || "").trim(),
                  productCategory: p.atcProductCategoryCode || "",
                  productDetail: p.atcProductDetailCode || "",
                  netShippingWeight: String(p.atcCertifiedWeight || ""),
                  supplementaryWeight: "0",
                }))
              : []
          );

          setAtcRawMaterialInfo(
            Array.isArray(data.rawMaterialInformation)
              ? data.rawMaterialInformation.map((r: any) => ({
                  associatedProductNumber: (r.associatedProductNo || "").trim(),
                  rawMaterialCode: r.rawMaterialCode || "",
                  rawMaterialComposition: r.rawMaterialCompostion || "",
                  countryArea: r.country || "",
                  stateProvince: r.state || "",
                  rawMaterialWeight: String(r.rawMaterialWeight || ""),
                }))
              : []
          );
        } else {
          notify("Failed to fetch ATU details", "error");
        }
      } catch (err) {
        console.error("Error fetching ATC:", err);
        notify("Error fetching ATU details", "error");
      }
    };

    loadATCData();
  }, [atcId]);

  // ===================== Stepper Navigation =====================
  const handleNext = async () => {
    if (activeStep === 2) {
      if (!atcProductInfoDetails || atcProductInfoDetails.length === 0) {
        notify("Please add at least one Product Information", "error");
        return;
      }

      const duplicateProducts = findDuplicateProducts();
      if (duplicateProducts.length > 0) {
        notify("Product number already exists", "error");
        return;
      }
    }

    // Standard behavior for steps before the last
    if (activeStep < steps.length - 1) {
      const currentRef = formRefs[activeStep]?.current;

      // If ref exists and has a validateForm function, validate first
      if (currentRef && typeof currentRef.validateForm === "function") {
        try {
          const errors = (await currentRef.validateForm()) || {};
          Object.keys(errors).forEach((fieldName) => {
            if (fieldName === "uploadedFile") {
              notify(errors[fieldName], "error");
            }
            currentRef.setFieldTouched(fieldName, true, false);
          });
          if (Object.keys(errors).length > 0) {
            notify("Please fill in the required values", "error");
            return;
          }

          // only submit if submitForm exists
          if (typeof currentRef.submitForm === "function") {
            await currentRef.submitForm();
          }

          if (activeStep === 2) {
            const duplicateProducts = findDuplicateProducts();
            if (duplicateProducts.length > 0) {
              notify("Product number already exists", "error");
              return;
            }
          }

          setActiveStep((prev) => prev + 1);
        } catch (err) {
          console.error("Validation error:", err);
          notify("Validation failed", "error");
        }
      } else {
        // No ref or no validateForm available -> fallback to simply advance
        setActiveStep((prev) => prev + 1);
      }
    } else {
      // Last step behaviour: require at least one raw material row (you already had this)
      if (atcRawMaterialInfo.length > 0) {
        await handleFinish();
      } else {
        notify("Please add at least one Raw Material detail", "error");
      }
    }
  };

  const normalizeProductNumber = (value?: string | null) =>
    (value ?? "").toString().trim();

  const findDuplicateProducts = () => {
    const counts = new Map<string, number>();
    atcProductInfoDetails.forEach((product) => {
      const normalized = normalizeProductNumber(product.productNumber);
      if (!normalized) return;
      const key = normalized.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([key]) => key);
  };

  const findProductsMissingRaw = () => {
    const normalizedProducts = atcProductInfoDetails
      .map((product) => normalizeProductNumber(product.productNumber))
      .filter(Boolean);

    return normalizedProducts.filter(
      (productNo) =>
        !atcRawMaterialInfo.some(
          (raw) =>
            normalizeProductNumber(raw.associatedProductNumber) === productNo
        )
    );
  };

  // In the handleFinish function, replace the TC number validation section with this:

  const handleFinish = async () => {
    const rawHasUnsaved = Boolean(
      (rawMaterialInfoRef.current as any)?.hasUnsavedRow
    );
    if (rawHasUnsaved) {
      notify(
        "Please save the current Raw Material row before submitting",
        "error"
      );
      return;
    }
    if (atcRawMaterialInfo.length === 0) {
      notify("Please add at least one Raw Material Information", "error");
      return;
    }

    const duplicateProducts = findDuplicateProducts();
    if (duplicateProducts.length > 0) {
      notify("Product number already exists", "error");
      return;
    }

    const missingRawProducts = findProductsMissingRaw();
    if (missingRawProducts.length > 0) {
      notify("Add raw material details for remaining products", "error");
      return;
    }

    const uploadType = atcCertificateDetails.uploadType || "GOTS";
    let atcNumber = atcCertificateDetails.transactionCertificateNumber
      .trim()
      .toUpperCase();

    // Remove any invalid characters (only keep A-Z, 0-9, and dashes)
    atcNumber = atcNumber.replace(/[^A-Z0-9-]/g, "");

    // Validate that uploadType is either ATU or GOTS
    if (!["ATU", "GOTS"].includes(uploadType)) {
      notify("Upload Type must be either ATU or GOTS", "error");
      return;
    }

    let atcNoNormalized = "";

    // Check if the stored value already starts with the correct upload type
    const uploadPrefix = `${uploadType}-`;

    if (atcNumber.startsWith(uploadPrefix)) {
      // Already has the correct prefix, use as-is
      atcNoNormalized = atcNumber;
    } else if (atcNumber.startsWith("ATU-") || atcNumber.startsWith("GOTS-")) {
      // Has a different upload type prefix, replace it
      const withoutPrefix = atcNumber.substring(atcNumber.indexOf("-") + 1);
      atcNoNormalized = `${uploadType}-${withoutPrefix}`;
    } else {
      // No upload type prefix, add it
      atcNoNormalized = `${uploadType}-${atcNumber}`;
    }

    // Final validation: ensure it starts with ATU or GOTS and contains only valid characters
    if (!atcNoNormalized.match(/^(ATU|GOTS)-[A-Z0-9-]+$/)) {
      notify(
        "Transaction Certificate Number format is invalid. It must start with ATU or GOTS followed by a dash and contain only letters, numbers, and dashes.",
        "error"
      );
      return;
    }

    // Additional check: ensure it's not just the prefix
    if (
      atcNoNormalized === `${uploadType}-` ||
      atcNoNormalized.length <= uploadType.length + 1
    ) {
      notify(
        "Transaction Certificate Number must contain content after the upload type prefix.",
        "error"
      );
      return;
    }

    // Certified raw material totals may be less than the product certified weight.
    // The only hard stop here is if they exceed it.
    const TOLERANCE_CENTS = 1; // 1 = 0.01 units
    const adjustedRawMaterials: ATRawMaterialInfo[] = atcRawMaterialInfo.map((r) => ({ ...r }));
    const overweightProducts: string[] = [];

    atcProductInfoDetails.forEach((product) => {
      const productNo = normalizeProductNumber(product.productNumber);
      if (!productNo) {
        return;
      }

      const net = Number(product.netShippingWeight) || 0;
      const supp = Number(product.supplementaryWeight) || 0;
      const productWeightCents = Math.round(Math.max(0, net - supp) * 100);

      if (!productWeightCents) {
        return;
      }

      const rmIndexesForProduct: number[] = [];
      adjustedRawMaterials.forEach((rm, idx) => {
        if (normalizeProductNumber(rm.associatedProductNumber) === productNo) {
          rmIndexesForProduct.push(idx);
        }
      });

      if (rmIndexesForProduct.length === 0) {
        return;
      }

      const rawTotalCents = rmIndexesForProduct.reduce((sum, idx) => {
        const weight = Number(adjustedRawMaterials[idx].rawMaterialWeight) || 0;
        return sum + Math.round(weight * 100);
      }, 0);

      const overflowCents = rawTotalCents - productWeightCents;
      if (overflowCents <= 0) {
        return;
      }

      if (overflowCents <= TOLERANCE_CENTS) {
        const lastIdx = rmIndexesForProduct[rmIndexesForProduct.length - 1];
        const lastWeight = Number(adjustedRawMaterials[lastIdx].rawMaterialWeight) || 0;
        const adjustedLastWeight = Math.round(lastWeight * 100) - overflowCents;

        if (adjustedLastWeight < 0) {
          overweightProducts.push(
            `Product ${productNo}: Product weight ${(productWeightCents / 100).toFixed(
              2
            )}, Raw Total ${(rawTotalCents / 100).toFixed(2)}`
          );
          return;
        }

        adjustedRawMaterials[lastIdx] = {
          ...adjustedRawMaterials[lastIdx],
          rawMaterialWeight: (adjustedLastWeight / 100).toFixed(2),
        };
        return;
      }

      overweightProducts.push(
        `Product ${productNo}: Product weight ${(productWeightCents / 100).toFixed(
          2
        )}, Raw Total ${(rawTotalCents / 100).toFixed(2)}`
      );
    });

    if (overweightProducts.length > 0) {
      notify(
        `Certified raw material weight cannot exceed the certified product weight for:\n${overweightProducts.join(
          "\n"
        )}`,
        "error"
      );
      return;
    }

    if (JSON.stringify(adjustedRawMaterials) !== JSON.stringify(atcRawMaterialInfo)) {
      setAtcRawMaterialInfo(adjustedRawMaterials);
    }

    // --- continue with your existing payload build ---
    const plain = (label?: string) =>
      label ? label.replace(/\s*\(.*\)$/, "") : "";

    const normalizedBlobName = atcCertificateDetails.uploadedFile?.name
    ? atcCertificateDetails.uploadedFile.name.replace(/\s+/g, "_")
    : "";

    const apiPayload: ATCUpsertPayload = {
      atcNo: atcNoNormalized, // use the normalized number
      atcVersionNo: atcCertificateDetails.tcVersionNumber,
      uploadType: atcCertificateDetails.uploadType,
      tcUploadCb: atcCertificateDetails.tcUploadCb,
      atcCbLicensingCode:atcCertificateDetails.cbLicensingCode,
      atcStandard: atcCertificateDetails.tcStandard,
      atcDateOfIssue: atcCertificateDetails.dateOfIssue,
      atcSubmissionDate:
        atcCertificateDetails.submissionDate ||
        atcCertificateDetails.dateOfIssue,
      blobPath: normalizedBlobName || "",
      sellerName: atcBuyerSellerDetails.sellerName,
      sellerTeid: atcBuyerSellerDetails.sellerTeId,
      sellerAddress: atcBuyerSellerDetails.sellerAddress,
      sellerState: atcBuyerSellerDetails.sellerState || "",
      sellerCountry: atcBuyerSellerDetails.sellerCountry || "",
      sellerPostalCode: atcBuyerSellerDetails.sellerPostalCode || "",
      buyerName: atcBuyerSellerDetails.buyerName,
      buyerTeid: atcBuyerSellerDetails.buyerTeId,
      buyerAddress: atcBuyerSellerDetails.buyerAddress,
      buyerScNo: atcBuyerSellerDetails.buyerScNumber,
      buyerState: atcBuyerSellerDetails.buyerState || "",
      buyerCountry: atcBuyerSellerDetails.buyerCountry || "",
      buyerPostalCode: atcBuyerSellerDetails.buyerPostalCode || "",
      productInformation: atcProductInfoDetails.map((p) => {
        const netShipping = Number(
          (parseFloat(String(p.netShippingWeight)) || 0).toFixed(2)
        );
        const supplementary = Number(
          (parseFloat(String(p.supplementaryWeight)) || 0).toFixed(2)
        );

        return {
          atcProductNo: p.productNumber,
          atcProductCategoryCode: p.productCategory,
          atcProductDetailCode: p.productDetail,
          // Backend expects field names: netShippingWeight and supplementaryWeight
          netShippingWeight: netShipping,
          supplementaryWeight: supplementary,
        };
      }),
      // Use the *adjusted* raw materials so the tiny tolerance fix is sent to the API
      rawMaterialInformation: adjustedRawMaterials.map((r) => ({
        associatedProductNo: r.associatedProductNumber,
        rawMaterialCode: r.rawMaterialCode,
        rawMaterialCompostion:
          parseFloat(String(r.rawMaterialComposition)) || 0.0,
        country: Array.isArray(r.countryArea)
          ? r.countryArea
          : r.countryArea
          ? [r.countryArea]
          : [],

        countryCode: Array.isArray(r.countryArea)
          ? r.countryArea
          : r.countryArea
          ? [r.countryArea]
          : [],

        state: Array.isArray(r.stateProvince)
          ? r.stateProvince
          : r.stateProvince
          ? [r.stateProvince]
          : [],

        stateCode: Array.isArray(r.stateProvince)
          ? r.stateProvince
          : r.stateProvince
          ? [r.stateProvince]
          : [],
        // Parse the string with 2 decimals into a float for the API
        rawMaterialWeight: parseFloat(String(r.rawMaterialWeight)) || 0.0,
      })),
    };

    try {
      const resp = await atcService.saveATCDetails(apiPayload);

      // Successful save -> same behavior
      if (resp?.status === 200 || resp?.status === 201) {
        notify("ATU Details Saved Successfully", "success");
        navigate("/atc");
        return;
      }

      // If backend returned a structured response with an error string:
      const errorMsg = (resp?.data as any)?.response || "Failed to save ATU details";

      // --- NEW: treat "Selected standards ..." server message as a NON-FATAL warning ---
      // If the server replies with text that starts with "Selected standards",
      // show it as a warning and proceed to navigate back (so user isn't blocked).
      if (typeof errorMsg === "string" && errorMsg.startsWith("Selected standards")) {
        notify(errorMsg, "warning");
        navigate("/atc");
        return;
      }

      // Keep existing "Current Standard" behavior: bring user back to step 0
      notify(errorMsg, "error");
      if (typeof errorMsg === "string" && errorMsg.includes("Current Standard")) {
        setActiveStep(0);
      }
    } catch (error: any) {
      console.error("Error saving ATC details:", error);

      const errorMsg =
        error?.response?.data?.response ||
        error?.response?.data?.message ||
        "Failed to save ATU details";

      // Same non-fatal handling in the catch path (if backend throws)
      if (typeof errorMsg === "string" && errorMsg.startsWith("Selected standards")) {
        notify(errorMsg, "warning");
        navigate("/atc");
        return;
      }

      notify(errorMsg, "error");

      if (typeof errorMsg === "string" && errorMsg.includes("Current Standard")) {
        setActiveStep(0);
      }
    }
  };

  const handleBack = () => {
    // Check for unsaved rows in Raw Material Information (step 3)
    if (activeStep === 3) {
      const rawHasUnsaved = Boolean(
        (rawMaterialInfoRef.current as any)?.hasUnsavedRow
      );
      if (rawHasUnsaved) {
        notify(
          "Please save or cancel the current Raw Material row before navigating back",
          "error"
        );
        return;
      }
    }
    // Check for unsaved rows in Product Information (step 2)
    if (activeStep === 2) {
      const productHasUnsaved = Boolean(
        (productInfoRef.current as any)?.hasUnsavedRow
      );
      if (productHasUnsaved) {
        notify(
          "Please save or cancel the current Product Information row before navigating back",
          "error"
        );
        return;
      }
    }
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const atcProductNum = atcProductInfoDetails
    .map((prod) => normalizeProductNumber(prod?.productNumber))
    .filter(Boolean);

  const productWeightsMap = atcProductInfoDetails.reduce((acc, product) => {
    const normalizedNumber = normalizeProductNumber(product.productNumber);
    if (!normalizedNumber) {
      return acc;
    }
    const net = Number(product.netShippingWeight) || 0;
    const supp = Number(product.supplementaryWeight) || 0;
    const weight = Math.max(0, net - supp);
    acc[normalizedNumber] = weight;
    return acc;
  }, {} as Record<string, number>);

  const productsMissingRaw = findProductsMissingRaw();
  const hasMissingRawMaterials =
    atcProductNum.length > 0 && productsMissingRaw.length > 0;

  // ===================== Steps =====================
  const steps = [
    {
      label: "Transaction Certificate Details",
      content: (
        <TransactionCertificateDetailsForm
          formikRef={certificateDetailsRef}
          detailsData={atcCertificateDetails}
          setDetailsData={setAtcCertificateDetails}
        />
      ),
    },
    {
      label: "Buyer & Seller Details",
      content: (
        <BuyerSellerDetailsForm
          formRef={buyerSellerDetailsRef}
          detailsData={atcBuyerSellerDetails}
          setDetailsData={setAtcBuyerSellerDetails}
        />
      ),
    },
    {
      label: "Product Information",
      content: (
        <ProductInformationForm
          formRef={productInfoRef}
          detailsData={atcProductInfoDetails}
          setDetailsData={setAtcProductInfoDetails}
        />
      ),
    },
    {
      label: "Raw Material Information",
      content: (
        <RawMaterialInformationForm
          formRef={rawMaterialInfoRef}
          detailsData={atcRawMaterialInfo}
          setDetailsData={setAtcRawMaterialInfo}
          productNumbers={atcProductNum}
          productWeightsMap={productWeightsMap}
          selectedStandards={atcCertificateDetails.tcStandard}
        />
      ),
    },
  ];

  // ===================== Actions =====================
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  // Minimal gating: prevent navigation when there are unsaved edits in steps 2/3,
  // or no saved rows on those steps. Do not change other behavior.
  const productHasUnsaved = Boolean(
    (productInfoRef.current as any)?.hasUnsavedRow
  );
  const rawHasUnsaved = Boolean(
    (rawMaterialInfoRef.current as any)?.hasUnsavedRow
  );
  const nextDisabled =
    (activeStep === 2 &&
      (productHasUnsaved ||
        !atcProductInfoDetails ||
        atcProductInfoDetails.length === 0)) ||
    (activeStep === 3 &&
      (rawHasUnsaved ||
        !atcRawMaterialInfo ||
        atcRawMaterialInfo.length === 0 ||
        hasMissingRawMaterials));

  const actions = [
    ...(!isFirstStep
      ? [
          {
            label: "Back",
            onClick: handleBack,
            variant: "contained" as const,
            color: "inherit" as const,
          },
        ]
      : []),
    ...(isFirstStep
      ? [
          {
            label: "Cancel",
            onClick: () => setDialogOpen(true),
            variant: "outlined" as const,
            color: "secondary" as const,
          },
        ]
      : []),
    {
      label: isLastStep ? "Submit" : "Next",
      onClick: isLastStep ? handleFinish : handleNext,
      variant: "contained" as const,
      color: "primary" as const,
      disabled: nextDisabled,
    },
  ];

  useEffect(() => {
    if (activeStep === 1 && buyerSellerDetailsRef.current) {
      const ref = buyerSellerDetailsRef.current;

      // Seller and Buyer Names: disable if value exists
      const sellerNameDisabled = !!atcBuyerSellerDetails.sellerName;
      const buyerNameDisabled = !!atcBuyerSellerDetails.buyerName;

      ref.setDisable?.({
        sellerName: sellerNameDisabled,
        sellerAddress: !!atcBuyerSellerDetails.sellerAddress,
        sellerCbLicensingCode: false, // always editable
        buyerName: buyerNameDisabled,
        buyerAddress: !!atcBuyerSellerDetails.buyerAddress,
        buyerScNumber: false, // always editable
      });

      ref.setVisible?.({
        sellerName: !atcBuyerSellerDetails.sellerName,
        buyerName: !atcBuyerSellerDetails.buyerName,
        sellerTeIdLabel: !atcBuyerSellerDetails.sellerTeId,
        buyerTeIdLabel: !atcBuyerSellerDetails.buyerTeId,
      });
    }
  }, [activeStep, atcBuyerSellerDetails]);

  // ===================== Cancel =====================
  const handleCancelSave = () => setDialogOpen(false);

  const handleCancelAmendment = async () => {
    // Uncomment below for cancel API
    /*
    try {
      await atcService.cancelATC(ATC_ID);
      notify("Cancelled Successfully", "success");
      navigate("/atc-dashboard");
    } catch (err) {
      notify("Failed to cancel", "error");
    }
    */

    notify("Cancelled Successfully", "success");
    navigate("/atc");
    setDialogOpen(false);
  };

  // ===================== Render =====================
  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={(step: number) => setActiveStep(step)}
            isNonLinear={true}
          />

          <Box
            sx={{
              marginTop: "3rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {actions.map((action, index) => (
              <StyledButton
                key={index}
                onClick={action.onClick}
                variant={action.variant || "text"}
                disabled={false}
                color={action.color}
              >
                {action.label}
              </StyledButton>
            ))}
          </Box>

          <AlertPopup
            open={dialogOpen}
            title="Cancel Confirmation"
            message="Are you sure you want to cancel?"
            onClose={handleCancelSave}
            onConfirm={handleCancelAmendment}
            confirmText="Yes, Cancel"
            cancelText="No"
          />
        </>
      )}
    </>
  );
};
export default AlternativeTransactionCertificate;
